"use client";

import { useRef, useState, useSyncExternalStore } from "react";
import { CalendarClock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  MAINTENANCE_NOTICE,
  type MaintenanceNoticeStatus,
} from "@/lib/maintenance-notice";

const STORAGE_KEY = `home-notice-popup-dismissed:${MAINTENANCE_NOTICE.id}`;

function readDismissedDay(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function subscribeToDismissedDay(onStoreChange: () => void) {
  function onStorage(event: StorageEvent) {
    if (event.key === STORAGE_KEY || event.key === null) onStoreChange();
  }
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

function getServerDismissedDay(): null {
  return null;
}

interface MaintenanceNoticePopupProps {
  status: MaintenanceNoticeStatus | null;
}

export default function MaintenanceNoticePopup({
  status,
}: MaintenanceNoticePopupProps) {
  const [dismissedInThisVisit, setDismissedInThisVisit] = useState(false);
  const previousFocus = useRef<HTMLElement | null>(null);
  const dismissedDay = useSyncExternalStore(
    subscribeToDismissedDay,
    readDismissedDay,
    getServerDismissedDay,
  );
  const phase = status?.phase;
  const dayKey = status?.dayKey;
  const active = phase === "upcoming" || phase === "ongoing";

  const open = active && !!dayKey && !dismissedInThisVisit && dismissedDay !== dayKey;

  function close() {
    setDismissedInThisVisit(true);
  }

  function dismissToday() {
    if (dayKey) {
      try {
        window.localStorage.setItem(STORAGE_KEY, dayKey);
      } catch {
        // 저장소 접근이 막혀 있어도 현재 방문에서는 닫을 수 있다.
      }
    }
    close();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <DialogContent
        data-nosnippet
        className="z-[61] max-h-[90dvh] overflow-y-auto overscroll-contain text-foreground motion-reduce:animate-none! sm:max-w-md"
        overlayClassName="z-[60] motion-reduce:animate-none!"
        showCloseButton={false}
        onOpenAutoFocus={() => {
          previousFocus.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          if (previousFocus.current?.isConnected) {
            event.preventDefault();
            previousFocus.current.focus({ preventScroll: true });
          }
        }}
      >
        <DialogClose asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="서버 점검 및 업데이트 안내 닫기"
            className="absolute right-2 top-2 size-11 touch-manipulation transition-colors"
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </DialogClose>

        <DialogHeader className="items-center pt-2 text-center sm:text-center">
          <span className="mb-1 grid size-12 place-items-center rounded-2xl bg-muted text-foreground">
            <CalendarClock className="size-6" aria-hidden="true" />
          </span>
          <DialogTitle className="text-balance text-xl leading-snug">
            {MAINTENANCE_NOTICE.title}
          </DialogTitle>
          <DialogDescription className="text-pretty text-sm leading-relaxed">
            {MAINTENANCE_NOTICE.description}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border bg-muted/50 p-4 text-center">
          <p className="text-xs font-medium text-muted-foreground" aria-live="polite">
            {phase === "ongoing" ? "작업 시간" : "작업 예정 시간"}
          </p>
          <p className="mt-2 text-balance font-semibold">
            <time dateTime={MAINTENANCE_NOTICE.startsAt}>
              {MAINTENANCE_NOTICE.dateLabel}
            </time>
          </p>
          <p className="mt-1 text-balance text-lg font-bold tabular-nums">
            {MAINTENANCE_NOTICE.timeLabel}
          </p>
        </div>

        <p className="text-center text-sm leading-relaxed text-muted-foreground">
          이용에 참고해 주세요.
          <br />
          불편을 드려 죄송합니다.
        </p>

        <div className="grid gap-1">
          <Button
            type="button"
            onClick={close}
            className="min-h-11 touch-manipulation transition-colors"
          >
            확인
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={dismissToday}
            className="min-h-11 touch-manipulation text-xs text-muted-foreground transition-colors"
          >
            오늘 하루 보지 않기
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
